import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buildPath, parsePath, redirectTo } from '../routing';
import { changeLanguage, getSavedLanguage, setSavedLanguage } from '../i18n';
import { detectBrowserLanguage, getDefaultLanguage, normalizeLanguage } from '../i18n/utils';
import { fetchGeoData, getCachedGeoData } from '../geo';

export function useLanguageRouting() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const { lang: pathLang, route } = parsePath(window.location.pathname);
    const hash = window.location.hash || '';
    const savedLang = getSavedLanguage();
    const currentLang = normalizeLanguage(i18n.language);

    if (pathLang) {
      if (currentLang !== pathLang) {
        changeLanguage(pathLang);
      } else if (savedLang !== pathLang) {
        setSavedLanguage(pathLang);
      }
      document.documentElement.lang = pathLang;
      return;
    }

    if (savedLang) {
      redirectTo(`${buildPath(savedLang, route)}${hash}`);
      return;
    }

    const detected = detectBrowserLanguage();
    if (detected) {
      changeLanguage(detected, { persist: false });
      document.documentElement.lang = detected;
      redirectTo(`${buildPath(detected, route)}${hash}`);
      return;
    }

    const cachedGeo = getCachedGeoData();
    if (cachedGeo) {
      const geoLang = cachedGeo.countryCode === 'PL' ? 'pl' : 'en';
      changeLanguage(geoLang, { persist: false });
      document.documentElement.lang = geoLang;
      redirectTo(`${buildPath(geoLang, route)}${hash}`);
      return;
    }

    fetchGeoData()
      .then(data => {
        const geoLang = data.countryCode === 'PL' ? 'pl' : 'en';
        changeLanguage(geoLang, { persist: false });
        document.documentElement.lang = geoLang;
        redirectTo(`${buildPath(geoLang, route)}${hash}`);
      })
      .catch(() => {
        const fallback = getDefaultLanguage();
        changeLanguage(fallback, { persist: false });
        document.documentElement.lang = fallback;
        redirectTo(`${buildPath(fallback, route)}${hash}`);
      });
  }, [i18n]);
}
